import { z } from 'zod'
import { mcpAccessLevelSchema } from '../enums/mcp-access-level.enum.js'
import { mcpPrincipalKindSchema } from '../enums/mcp-principal-kind.enum.js'
import { mcpOrgSettingSchema } from '../enums/mcp-org-setting.enum.js'
import { recognizedAppMatchSchema } from '../enums/recognized-app-match.enum.js'
import { uuidSchema } from '../validators/params.js'

/** The id an AI app chooses for an action that creates envelopes or sends email; a retry with the same id replays the first result. */
export const mcpRequestIdSchema = z.string().regex(/^[\x21-\x7E]{1,256}$/, 'requestId must be 1–256 printable ASCII characters')

/** Six digits from the email PixelSign sends before live or staff-change access. */
export const grantCodeSchema = z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from the email')

/** Anything except live changes. A new access level stays here until it is live changes. */
const nonLiveAccessLevelSchema = mcpAccessLevelSchema.exclude(['live'])

const memberGrantSchema = z.object({ tenantId: uuidSchema, level: mcpAccessLevelSchema }).strict()
const nonLiveGrantSchema = z.object({ tenantId: uuidSchema, level: nonLiveAccessLevelSchema }).strict()

/**
 * Approving a connection. Read-only member grants and staff read-only carry
 * no code. A grant that includes live changes, and staff "make changes",
 * require the emailed code; live changes also require the acknowledgement.
 * A code may still be sent when it is not required.
 */
const memberReadApproveSchema = z
  .object({
    as: z.literal('tenant_user'),
    grants: z.array(nonLiveGrantSchema).max(200),
    acknowledged: z.boolean().default(false),
    code: grantCodeSchema.optional(),
  })
  .strict()

const memberLiveApproveSchema = z
  .object({
    as: z.literal('tenant_user'),
    grants: z.array(memberGrantSchema).max(200),
    acknowledged: z.literal(true),
    code: grantCodeSchema,
  })
  .strict()

const staffReadApproveSchema = z
  .object({
    as: z.literal('staff'),
    changes: z.literal(false),
    code: grantCodeSchema.optional(),
  })
  .strict()

const staffChangesApproveSchema = z
  .object({
    as: z.literal('staff'),
    changes: z.literal(true),
    code: grantCodeSchema,
  })
  .strict()

export const consentApproveInputSchema = z.union([
  memberReadApproveSchema,
  memberLiveApproveSchema,
  staffReadApproveSchema,
  staffChangesApproveSchema,
])

export type ConsentApproveInput = z.infer<typeof consentApproveInputSchema>

/** Asking for the emailed code that live or staff-change access needs. */
export const consentCodeInputSchema = z.object({ as: mcpPrincipalKindSchema }).strict()

export type ConsentCodeInput = z.infer<typeof consentCodeInputSchema>

/** An Owner changing the organization's MCP setting. */
export const updateMcpSettingInputSchema = z.object({ setting: mcpOrgSettingSchema }).strict()

export type UpdateMcpSettingInput = z.infer<typeof updateMcpSettingInputSchema>

/** Staff recognizing an AI app (the value is checked further by validateRecognizedAppValue). */
export const addRecognizedAppInputSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    match: recognizedAppMatchSchema,
    value: z.string().trim().min(1).max(500),
  })
  .strict()

export type AddRecognizedAppInput = z.infer<typeof addRecognizedAppInputSchema>
